import React from 'react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaRocket, FaHandshake, FaUsers, FaChartLine, FaGlobe } from 'react-icons/fa';

const AboutPage = () => {
  const values = [
    {
      id: 1,
      title: 'Innovation',
      description: 'We embrace cutting-edge technologies and creative thinking to deliver innovative solutions.',
      icon: <FaLightbulb className="text-primary-500" size={30} />,
    },
    {
      id: 2,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from code quality to client communication.',
      icon: <FaRocket className="text-primary-500" size={30} />,
    },
    {
      id: 3,
      title: 'Integrity',
      description: 'We build relationships based on trust, transparency, and ethical business practices.',
      icon: <FaHandshake className="text-primary-500" size={30} />,
    },
    {
      id: 4,
      title: 'Collaboration',
      description: 'We believe in the power of teamwork and collaborative problem-solving.',
      icon: <FaUsers className="text-primary-500" size={30} />,
    },
    {
      id: 5,
      title: 'Growth',
      description: 'We are committed to continuous learning and professional development.',
      icon: <FaChartLine className="text-primary-500" size={30} />,
    },
    {
      id: 6,
      title: 'Global Perspective',
      description: 'We embrace diversity and bring a global perspective to every project.',
      icon: <FaGlobe className="text-primary-500" size={30} />,
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-secondary-900 to-secondary-800 text-white">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1
              className="mb-6 text-4xl font-bold md:text-5xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              About FutureSoft Technologies
            </motion.h1>
            <motion.p
              className="text-lg text-secondary-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              We're a team of passionate developers, designers, and strategists dedicated to creating exceptional software solutions that drive business growth.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="mb-6 text-3xl font-bold">Our Story</h2>
              <p className="mb-4 text-secondary-700">
                Founded in 2020, FutureSoft Technologies began with a simple mission: to help businesses leverage technology to achieve their goals. What started as a small team of three developers has grown into a full-service software development company serving clients across the globe.
              </p>
              <p className="mb-4 text-secondary-700">
                Our journey has been defined by continuous learning, adaptation, and a relentless focus on delivering value to our clients. We've weathered challenges, celebrated successes, and built lasting relationships along the way.
              </p>
              <p className="mb-4 text-secondary-700">
                Today, we're proud to be trusted partners for businesses ranging from innovative startups to established enterprises. Our diverse portfolio spans various industries, including healthcare, finance, e-commerce, and education.
              </p>
              <p className="text-secondary-700">
                As we look to the future, we remain committed to our founding principles while embracing new technologies and methodologies that enable us to deliver even greater value to our clients.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="flex items-center justify-center"
            >
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1050&q=80"
                alt="Team collaboration"
                className="rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="section bg-secondary-50">
        <div className="container">
          <div className="mb-12 text-center">
            <h2 className="section-title">Our Core Values</h2>
            <p className="max-w-2xl mx-auto text-lg text-secondary-600">
              These principles guide everything we do and shape how we work with our clients and each other.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {values.map((value) => (
              <motion.div
                key={value.id}
                className="p-6 bg-white rounded-lg shadow-md"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center justify-center w-16 h-16 mb-4 bg-primary-50 rounded-lg">
                  {value.icon}
                </div>
                <h3 className="mb-2 text-xl font-semibold">{value.title}</h3>
                <p className="text-secondary-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
